import { fetchWithMemoryCache } from "../../utils/cache/memory";
import { createHashKey } from "../../utils";
import { withHandle, withLocale, withProductCursor } from "../../types/fetch";
import { clientFetch } from "../sanity/resource/helpers";
import { fetchShopifyProducts, fetchSanityProducts } from "./product";
import { ShopifySdk } from "../shopify";
import { sanity } from "../";
import {
  formatProducts,
  formatSanityProductIds,
  formatShopifyProductIds,
} from "../../utils/products";
import { SHOPIFY_DEFAULT_LOCATION } from "../../utils/shopify";
import { fetchLayout } from "./layout";

import { constructStaticPathsFromHandles } from "../../utils/paths";
const fetchShopifyCollection = async ({
  handle,
  productCursor,
}: withHandle & withProductCursor) => {
  const fetcher = () =>
    ShopifySdk.collectionByHandle({ handle, productCursor });
  const key = createHashKey("fetchShopifyCollection", {
    handle,
    productCursor,
  });
  return fetchWithMemoryCache(key, fetcher);
};

const fetchSanityCollection = ({ handle }: withHandle) => {
  const fetcher = () => sanity.collections.fetchByHandle({ handle });
  const key = createHashKey("fetchSanityCollection", { handle });
  return fetchWithMemoryCache(key, fetcher);
};

export const fetchCollection = async ({
  handle,
  locale,
  productCursor = null,
}: withHandle & withProductCursor & withLocale) => {
  // Fetch collections
  // const fetcher = async () => {
  const [shopifyCollection, sanityCollection] = await Promise.all([
    fetchShopifyCollection({ handle, productCursor }),
    fetchSanityCollection({ handle }),
  ]);

  // Fetch products
  const [shopifyProducts, sanityProducts]: [any, any] = await Promise.all([
    fetchShopifyProducts({
      ids: formatShopifyProductIds({
        shopifyProducts: shopifyCollection?.collectionByHandle?.products,
      }),
      countryCode: locale?.toUpperCase() || SHOPIFY_DEFAULT_LOCATION,
    }),
    fetchSanityProducts({
      ids: formatSanityProductIds({
        shopifyProducts: shopifyCollection?.collectionByHandle?.products,
      }),
    }),
  ]);

  // Order sanity products the same as as shopify
  const products = formatProducts({ shopifyProducts, sanityProducts });

  return {
    collection: sanityCollection,
    products,
    shopifyProducts,
    shopifyCollection,
    pagination: {
      handle,
      locale,
      endCursor:
        shopifyCollection?.collectionByHandle?.products?.pageInfo?.endCursor ||
        null,
      hasNextPage:
        shopifyCollection?.collectionByHandle?.products?.pageInfo
          ?.hasNextPage || null,
    },
  };
  // };

  // const key = createHashKey("fetchCollection", {
  //   handle,
  //   locale,
  //   productCursor,
  // });
  // return fetchWithMemoryCache(key, fetcher);
};

export const fetchCollectionPage = ({
  locale,
  preview,
  handle,
}: withLocale & { preview: boolean } & withHandle) => {
  const fetcher = async () => {
    if (!handle || handle == "error") throw new Error();

    const layout = await fetchLayout({ locale, isPreview: preview });
    const fetchedCollection = await fetchCollection({ handle, locale });

    if (!fetchedCollection.collection) throw new Error("Collection not found");

    const { collection, products, shopifyProducts, pagination } =
      fetchedCollection;

    return {
      handle,
      layout,
      preview,
      collection,
      products,
      shopifyProducts,
      pagination,
      locale,
    };
  };

  const key = createHashKey("fetchCollectionPage", { locale, handle, preview });
  return preview ? fetcher() : fetchWithMemoryCache(key, fetcher);
};

export const fetchCollectionPaths = async ({ locales }) => {
  const resources = await clientFetch({
    query: /* groq */ `
      *[_type == "collection"] {
        "handle": store.slug.current
      }
    `,
  });

  const handles = resources.map((resource: any) => resource.handle.toString());
  return constructStaticPathsFromHandles({ locales, handles });
};
